<template>
  <div class="process-page">
    <section class="process-hero">
      <div class="hero-copy">
        <p class="eyebrow">The tutoring process</p>
        <h1>Less guessing.<br><em>More getting it.</em></h1>
        <p>
          One clear path from “I’m stuck” to solving the next problem on your
          own—with every session built around your actual course.
        </p>
        <router-link to="/contact" class="hero-button">
          Start with a free 30-minute call <span aria-hidden="true">↗</span>
        </router-link>
      </div>

      <div class="hero-board" aria-label="The AliTutor learning loop">
        <div class="board-note note-one">
          <span>01</span>
          Find the gap
        </div>
        <div class="board-arrow" aria-hidden="true">↓</div>
        <div class="board-note note-two">
          <span>02</span>
          Work the logic
        </div>
        <div class="board-arrow" aria-hidden="true">↓</div>
        <div class="board-note note-three">
          <span>03</span>
          Own the method
        </div>
      </div>
    </section>

    <section class="steps-section">
      <header class="steps-heading">
        <p class="section-kicker">Three useful steps</p>
        <h2>No vague promises. Here’s exactly what happens.</h2>
      </header>

      <div class="steps">
        <article class="step">
          <div class="step-number">01</div>
          <div class="step-body">
            <p class="step-label">Free consultation · 30 minutes</p>
            <h3>Show me where it stops making sense.</h3>
            <p>
              We talk through your course, upcoming deadlines, and the exact
              concepts creating friction. Bring a syllabus, assignment, or just
              the question that has you stuck.
            </p>
            <ul>
              <li>Identify the highest-impact starting point</li>
              <li>Make sure the tutoring style feels right</li>
              <li>Leave with a clear next step—no pressure</li>
            </ul>
          </div>
        </article>

        <article class="step">
          <div class="step-number">02</div>
          <div class="step-body">
            <p class="step-label">Focused tutoring · One-on-one</p>
            <h3>Build the idea, then work the problem.</h3>
            <p>
              I explain the “why” in plain language, model the method, and guide
              you through relevant practice. We pause wherever the logic needs
              another angle.
            </p>
            <ul>
              <li>Lessons shaped around your class materials</li>
              <li>Live feedback while you practice</li>
              <li>A pace that responds to you</li>
            </ul>
          </div>
        </article>

        <article class="step">
          <div class="step-number">03</div>
          <div class="step-body">
            <p class="step-label">Independent practice · Real confidence</p>
            <h3>Prove it to yourself without my help.</h3>
            <p>
              You apply the method independently, explain your reasoning, and
              leave with a practical study plan for the next assignment or exam.
            </p>
            <ul>
              <li>Spot and correct patterns in mistakes</li>
              <li>Know what to practice next</li>
              <li>Build a process you can repeat</li>
            </ul>
          </div>
        </article>
      </div>
    </section>

    <section class="fit-section">
      <div class="fit-intro">
        <p class="section-kicker">A good fit if...</p>
        <h2>You want to understand—not just get through tonight’s homework.</h2>
      </div>
      <div class="fit-list">
        <p><span>01</span>You’re taking introductory or intermediate accounting.</p>
        <p><span>02</span>You want a patient place to ask the “obvious” question.</p>
        <p><span>03</span>You learn best by seeing the logic and practicing it.</p>
        <p><span>04</span>You’re ready to participate, not watch someone do the work.</p>
      </div>
    </section>

    <section class="process-cta">
      <p>Ready when you are.</p>
      <h2>Bring one question.<br>I’ll bring a clearer way through it.</h2>
      <router-link to="/contact">Find a free consultation time ↗</router-link>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.process-page {
  overflow: hidden;
  background: var(--paper);
  color: var(--ink);
}

.process-hero {
  display: grid;
  grid-template-columns: 1.08fr 0.92fr;
  min-height: 720px;
  background: var(--ink);
  color: white;
}

.hero-copy {
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: clamp(6rem, 10vw, 9rem) clamp(1.5rem, 8vw, 8rem);
}

.eyebrow,
.section-kicker {
  margin: 0 0 1.4rem;
  color: var(--amber);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  font-size: clamp(3.5rem, 7vw, 7.2rem);
  font-weight: 800;
  letter-spacing: -0.07em;
  line-height: 0.9;
}

h1 em {
  color: var(--amber);
  font-family: Georgia, "Times New Roman", serif;
  font-weight: 400;
}

.hero-copy > p:not(.eyebrow) {
  max-width: 610px;
  margin: 2.25rem 0;
  color: #cbd1df;
  font-size: 1.1rem;
  line-height: 1.75;
}

.hero-button {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 999px;
  background: white;
  color: var(--ink);
  font-weight: 800;
  transition: transform 180ms ease, background 180ms ease;
}

.hero-button:hover {
  background: var(--amber);
  transform: translateY(-2px);
}

.hero-board {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 5rem 2rem;
  background:
    linear-gradient(rgba(10, 16, 32, 0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(10, 16, 32, 0.07) 1px, transparent 1px),
    var(--amber);
  background-size: 44px 44px;
}

.board-note {
  width: min(360px, 92%);
  padding: 1.6rem 1.8rem;
  border: 2px solid var(--ink);
  border-radius: 16px;
  background: var(--paper);
  box-shadow: 9px 9px 0 var(--ink);
  color: var(--ink);
  font-size: 1.35rem;
  font-weight: 800;
  transform: rotate(-2deg);
}

.board-note span {
  display: block;
  margin-bottom: 0.45rem;
  color: var(--blue);
  font-size: 0.72rem;
  letter-spacing: 0.14em;
}

.note-two {
  background: var(--blue);
  color: white;
  transform: rotate(1.5deg);
}

.note-two span {
  color: var(--amber);
}

.note-three {
  background: var(--mint);
  transform: rotate(-1deg);
}

.board-arrow {
  margin: 0.65rem 0;
  color: var(--ink);
  font-size: 1.7rem;
  font-weight: 800;
}

.steps-section {
  width: min(1180px, calc(100% - 3rem));
  margin: 0 auto;
  padding: clamp(6rem, 11vw, 10rem) 0;
}

.steps-heading {
  display: grid;
  grid-template-columns: 0.4fr 1fr;
  gap: 2rem;
  margin-bottom: 5rem;
}

.steps-heading .section-kicker,
.fit-intro .section-kicker {
  color: var(--blue);
  padding-top: 0.65rem;
}

.steps-heading h2,
.fit-intro h2,
.process-cta h2 {
  max-width: 850px;
  margin: 0;
  font-size: clamp(2.6rem, 5vw, 5rem);
  letter-spacing: -0.06em;
  line-height: 0.98;
}

.steps {
  border-top: 1px solid #d9dde7;
}

.step {
  display: grid;
  grid-template-columns: 0.35fr 1fr;
  gap: 2rem;
  padding: clamp(3rem, 7vw, 6rem) 0;
  border-bottom: 1px solid #d9dde7;
}

.step-number {
  color: var(--blue);
  font-size: clamp(3.7rem, 8vw, 7rem);
  font-weight: 800;
  letter-spacing: -0.08em;
  line-height: 0.8;
}

.step-body {
  max-width: 800px;
}

.step-label {
  margin: 0 0 1rem !important;
  color: var(--blue) !important;
  font-size: 0.78rem !important;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.step-body h3 {
  margin: 0 0 1.3rem;
  font-size: clamp(2rem, 4vw, 3.4rem);
  letter-spacing: -0.05em;
  line-height: 1.05;
}

.step-body > p {
  max-width: 690px;
  color: #555d6d;
  font-size: 1.05rem;
  line-height: 1.8;
}

.step-body ul {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin: 2rem 0 0;
  padding: 0;
  list-style: none;
}

.step-body li {
  padding: 1.15rem;
  border-radius: 14px;
  background: white;
  color: #343b4a;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.55;
  box-shadow: 0 12px 35px rgba(10, 16, 32, 0.05);
}

.fit-section {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: clamp(3rem, 8vw, 8rem);
  padding: clamp(6rem, 11vw, 10rem) max(1.5rem, calc((100vw - 1180px) / 2));
  background: white;
}

.fit-list {
  border-top: 1px solid #d8dce5;
}

.fit-list p {
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 1rem;
  margin: 0;
  padding: 1.6rem 0;
  border-bottom: 1px solid #d8dce5;
  color: #343a48;
  font-size: 1.04rem;
  line-height: 1.55;
}

.fit-list span {
  color: var(--blue);
  font-size: 0.75rem;
  font-weight: 800;
}

.process-cta {
  padding: clamp(6rem, 12vw, 10rem) 1.5rem;
  background: var(--blue);
  color: white;
  text-align: center;
}

.process-cta p {
  margin: 0 0 1.3rem;
  color: #dce3ff;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.process-cta h2 {
  margin-right: auto;
  margin-left: auto;
}

.process-cta a {
  display: inline-flex;
  margin-top: 2.5rem;
  padding: 1rem 1.25rem;
  border-radius: 999px;
  background: var(--amber);
  color: var(--ink);
  font-weight: 800;
  transition: transform 180ms ease;
}

.process-cta a:hover {
  transform: translateY(-2px);
}

@media (max-width: 850px) {
  .process-hero,
  .steps-heading,
  .fit-section {
    grid-template-columns: 1fr;
  }

  .process-hero {
    min-height: 0;
  }

  .hero-copy {
    min-height: 620px;
    padding-right: 1.5rem;
    padding-left: 1.5rem;
  }

  .hero-board {
    min-height: 580px;
  }

  .step-body ul {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .steps-section {
    width: min(100% - 2rem, 1180px);
  }

  .step {
    grid-template-columns: 1fr;
  }

  .step-number {
    font-size: 4rem;
  }

  h1 {
    font-size: clamp(3.5rem, 16vw, 5.3rem);
  }
}
</style>
